import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { ProjectStats } from '../entities/project.stat';
import { StandaloneProjectSummary } from '../entities/standalone-project';
import { TrelloBoardStats } from '../entities/trello-board-stats';
import { ChatSessionMessage, getSessionMessages } from '../shared/api/chat-sessions';
import { getProjectStatOrCreate, getStandaloneProjectSummary, getTrelloBoardStats } from '../shared/api/dashboard';
import { useProjectById } from '../shared/contexts/Projects.provider';
import { useStandaloneProjects } from '../shared/hooks/use-standalone-projects';
import { useTrelloBoards } from '../shared/hooks/use-trello-boards';
import { asISODate } from '../shared/utils/stat-helpers';
import { getVirtualProjectLabel, isVirtualProjectId } from '../shared/utils/virtual-projects';
import SessionChatView, { ProjectMetrics } from '../widgets/chat/SessionChatView';

const DEFAULT_RANGE = (() => {
  const to = new Date();
  const from = new Date(to.getTime() - 30 * 24 * 60 * 60 * 1000);
  return { from: asISODate(from), to: asISODate(to) };
})();

function toMetrics(stat: ProjectStats): ProjectMetrics {
  return {
    psi: stat.psi,
    throughput: stat.summary.completedCount,
    avgCycleDays: stat.summary.avgCycleTimeHours / 24,
    reopenRate: stat.summary.reopenRatePercent,
  };
}

const SessionChatPage: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get('projectId') ?? '';

  const isVirtual = isVirtualProjectId(projectId);
  const project = useProjectById(projectId);
  const { boards: trelloBoards } = useTrelloBoards();
  const trelloBoard = trelloBoards.find((b) => b.id === projectId);
  const { projects: standaloneProjects } = useStandaloneProjects();
  const standaloneProject = standaloneProjects.find((p) => p._id === projectId);

  const [history, setHistory] = useState<ChatSessionMessage[]>([]);
  const [metrics, setMetrics] = useState<ProjectMetrics | null>(null);
  const [trelloMetrics, setTrelloMetrics] = useState<TrelloBoardStats | null>(null);
  const [standaloneMetrics, setStandaloneMetrics] = useState<StandaloneProjectSummary | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!sessionId || !projectId) return;
    setIsLoading(true);
    setError(null);
    setHistory([]);

    const statsPromise: Promise<
      | { kind: 'jira'; stat: ProjectMetrics }
      | { kind: 'trello'; stat: TrelloBoardStats }
      | { kind: 'standalone'; stat: StandaloneProjectSummary }
      | { kind: 'none' }
    > = trelloBoard
      ? getTrelloBoardStats(projectId, DEFAULT_RANGE)
          .then((r) => ({ kind: 'trello' as const, stat: r.stat }))
          .catch(() => ({ kind: 'none' as const }))
      : standaloneProject
        ? getStandaloneProjectSummary(projectId)
            .then((r) => ({ kind: 'standalone' as const, stat: r.stat }))
            .catch(() => ({ kind: 'none' as const }))
        : isVirtual
          ? Promise.resolve({ kind: 'none' as const })
          : getProjectStatOrCreate(projectId, DEFAULT_RANGE)
              .then((r) => ({ kind: 'jira' as const, stat: toMetrics(r.stat) }))
              .catch(() => ({ kind: 'none' as const }));

    Promise.all([getSessionMessages(sessionId), statsPromise])
      .then(([msgs, s]) => {
        setHistory(msgs);
        setMetrics(s.kind === 'jira' ? s.stat : null);
        setTrelloMetrics(s.kind === 'trello' ? s.stat : null);
        setStandaloneMetrics(s.kind === 'standalone' ? s.stat : null);
      })
      .catch(() => setError('Failed to load session.'))
      .finally(() => setIsLoading(false));
  }, [sessionId, projectId, isVirtual, trelloBoard, standaloneProject]);

  if (!sessionId) {
    return <div className="flex items-center justify-center h-full opacity-50">Session not found.</div>;
  }

  return (
    <div className="h-[calc(100vh-64px)] flex flex-col">
      {isLoading && (
        <div className="flex items-center justify-center flex-1">
          <span className="loading loading-spinner loading-lg opacity-40" />
        </div>
      )}

      {error && !isLoading && (
        <div className="flex items-center justify-center flex-1">
          <div className="alert alert-error max-w-sm">
            <span>{error}</span>
          </div>
        </div>
      )}

      {!isLoading && !error && (
        <SessionChatView
          sessionId={sessionId}
          projectId={projectId}
          projectName={
            getVirtualProjectLabel(projectId) ??
            trelloBoard?.name ??
            standaloneProject?.name ??
            project?.name ??
            projectId
          }
          history={history}
          metrics={metrics}
          trelloMetrics={trelloMetrics}
          standaloneMetrics={standaloneMetrics}
        />
      )}
    </div>
  );
};

export default SessionChatPage;
